import { useCallback, useState } from 'react';

import {
  type EmergencyStopRequest,
  EmergencyStopResponseSchema,
  type SubscriptionId,
} from '../../api';
import { type ApiError, toApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { useActionKeys } from '../../lib/api/idempotency-key';

import { markNoLongerOpen, mergeStop, type StopSummary } from './emergency-model';

/** What E10 shows after its writes. */
export interface EmergencyStopState {
  /** Every stop's answer gathered (M4 §4); `null` until the first stop succeeded (the sheet shows). */
  readonly summary: StopSummary | null;
  /** Failure of the last retry of a channel, per channel: its reason text shows on the row (I12). */
  readonly channelErrors: ReadonlyMap<SubscriptionId, ApiError>;
  /** A stop from the sheet (the first one, or "Retry closing") is in flight: the sheet is locked. */
  readonly stopping: boolean;
  /** Failure of the last stop from the sheet; the summary, if any, stays as it was. */
  readonly error: ApiError | null;
}

/** Actions of E10. */
export interface EmergencyStopActions {
  /** `POST /emergency-stop` with the trader's explicit choice (M4 R3: no default); true on success. */
  readonly stop: (request: EmergencyStopRequest) => Promise<boolean>;
  /**
   * Retries the pause of one failed channel: a new emergency stop with `closePositions: false`
   * (M4 §4 "Partial pause"), so the pause keeps the origin `emergency_stop` (R8) and is recorded
   * with the stop (R17). A paused channel stays as it is (R13).
   */
  readonly retryChannel: (subscriptionId: SubscriptionId) => Promise<void>;
  /** Forgets the last stop's failure, when the sheet opens again for "Retry closing". */
  readonly clearError: () => void;
  /**
   * The ids of a `GET /positions` read after the stop ("Retry closing"): a failed or running
   * close whose position is not among them reads "No longer open" (M4 §4, Q104).
   */
  readonly positionsRead: (openIds: ReadonlySet<string>) => void;
}

const INITIAL: EmergencyStopState = {
  summary: null,
  channelErrors: new Map(),
  stopping: false,
  error: null,
};

/** Only a pause, never a close: a channel's retry never closes what the trader did not choose (I7). */
const PAUSE_ONLY: EmergencyStopRequest = { closePositions: false };

/**
 * The writes of E10 (specs/M4-emergency-stop/spec.md §5). Every write carries an `Idempotency-Key`
 * kept across a retry after a lost answer (M4 R13, S2 R13), one per action: the stop with its
 * body, and each channel's retry. The stop never waits for a read: it needs neither `GET /me` nor
 * `GET /positions` (FXM-02 §7 "takes priority over everything").
 */
export function useEmergencyStop(): EmergencyStopState & EmergencyStopActions {
  const api = useApiClient();
  const keys = useActionKeys();
  const [state, setState] = useState<EmergencyStopState>(INITIAL);

  const send = useCallback(
    (request: EmergencyStopRequest, action: string) =>
      api.post(
        '/emergency-stop',
        { guard: { kind: 'authenticated', idempotencyKey: keys.keyFor(action) }, body: request },
        EmergencyStopResponseSchema,
      ),
    [api, keys],
  );

  const stop = useCallback(
    async (request: EmergencyStopRequest): Promise<boolean> => {
      const action = `stop:${JSON.stringify(request)}`;
      setState((current) => ({ ...current, stopping: true, error: null }));
      try {
        const answer = await send(request, action);
        keys.settle(action, null);
        setState((current) => ({
          ...current,
          summary: mergeStop(current.summary, answer, request.closePositions),
          stopping: false,
        }));
        return true;
      } catch (caught) {
        const error = toApiError(caught);
        keys.settle(action, error);
        setState((current) => ({ ...current, stopping: false, error }));
        return false;
      }
    },
    [keys, send],
  );

  const retryChannel = useCallback(
    async (subscriptionId: SubscriptionId): Promise<void> => {
      const action = `retry-pause:${subscriptionId}`;
      setState((current) => withRetrying(current, subscriptionId, true));
      try {
        const answer = await send(PAUSE_ONLY, action);
        keys.settle(action, null);
        setState((current) => {
          const channelErrors = new Map(current.channelErrors);
          channelErrors.delete(subscriptionId);
          // The answer names every channel; if it ever left this one out, its row still stops spinning.
          const merged = {
            ...current,
            summary: mergeStop(current.summary, answer, PAUSE_ONLY.closePositions),
          };
          return { ...withRetrying(merged, subscriptionId, false), channelErrors };
        });
      } catch (caught) {
        const error = toApiError(caught);
        keys.settle(action, error);
        setState((current) => {
          const channelErrors = new Map(current.channelErrors);
          channelErrors.set(subscriptionId, error);
          return { ...withRetrying(current, subscriptionId, false), channelErrors };
        });
      }
    },
    [keys, send],
  );

  const clearError = useCallback(() => {
    setState((current) => (current.error === null ? current : { ...current, error: null }));
  }, []);

  const positionsRead = useCallback((openIds: ReadonlySet<string>) => {
    setState((current) => {
      if (current.summary === null) return current;
      const summary = markNoLongerOpen(current.summary, openIds);
      return summary === current.summary ? current : { ...current, summary };
    });
  }, []);

  return { ...state, stop, retryChannel, clearError, positionsRead };
}

/** Marks the failed row of `subscriptionId` as retrying or not; any other row is left as it is. */
function withRetrying(
  state: EmergencyStopState,
  subscriptionId: SubscriptionId,
  retrying: boolean,
): EmergencyStopState {
  const row = state.summary?.rows.get(subscriptionId);
  if (state.summary === null || row?.kind !== 'failed') return state;
  const rows = new Map(state.summary.rows);
  rows.set(subscriptionId, { ...row, retrying });
  return { ...state, summary: { ...state.summary, rows } };
}

/** True while any write of E10 is in flight: the summary cannot be left and its buttons wait. */
export function isWriting(state: EmergencyStopState): boolean {
  if (state.stopping) return true;
  if (state.summary === null) return false;
  return [...state.summary.rows.values()].some((row) => row.kind === 'failed' && row.retrying);
}
